import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { 
  Menu, 
  X, 
  ChevronRight, 
  MapPin, 
  Clock, 
  Users, 
  Heart,
  Shield,
  GraduationCap,
  Star,
  Phone,
  Mail,
  CheckCircle,
  Truck,
  BookOpen,
  Calendar,
  Target,
  Sparkles,
  MessageCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Young puppy images
const HERO_IMAGE = "https://images.unsplash.com/photo-1591160690555-5debfba289f0?w=1200&q=80"; // Young golden retriever puppy
const PUPPY_TRAINING = "https://images.unsplash.com/photo-1751315722517-c2b5e2893079?w=800&q=80"; // Calm puppy resting in grass
const PUPPY_GROUP = "https://images.unsplash.com/photo-1532101783428-f654282bf06c?w=800&q=80"; // Well-behaved puppy portrait
const LOGO_IMAGE = "/api/uploads/logo_transparent.png";
const LOGO_IMAGE_DARK = "/api/uploads/logo_transparent.png";
const TRAINER_IMAGE = "https://customer-assets.emergentagent.com/job_premium-pack-3/artifacts/epct4rvq_20260308_161827.jpg";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isTrainer = user?.role === "trainer";

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? "bg-off-white/95 backdrop-blur-md shadow-sm" : "bg-charcoal/20 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="logo">
            <img 
              src={scrolled ? LOGO_IMAGE : LOGO_IMAGE_DARK} 
              alt="The Pack Standard Logo" 
              className="w-16 h-16 md:w-20 md:h-20 object-contain"
            />
            <span className={`font-heading text-lg md:text-xl font-semibold whitespace-nowrap leading-tight ${scrolled ? 'text-charcoal' : 'text-white'}`}>
              The Pack
              <span className="block">Standard</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/programs" className={`hover:text-olive transition-colors uppercase tracking-wide text-sm font-medium ${scrolled ? 'text-charcoal' : 'text-white'}`}>
              Programs
            </Link>
            <a href="#about" className={`hover:text-olive transition-colors uppercase tracking-wide text-sm font-medium ${scrolled ? 'text-charcoal' : 'text-white'}`}>
              About
            </a>
            <a href="#trainer" className={`hover:text-olive transition-colors uppercase tracking-wide text-sm font-medium ${scrolled ? 'text-charcoal' : 'text-white'}`}>
              Trainer
            </a>
            <Link to="/faq" className={`hover:text-olive transition-colors uppercase tracking-wide text-sm font-medium ${scrolled ? 'text-charcoal' : 'text-white'}`}>
              FAQ
            </Link>
            <a href="#contact" className={`hover:text-olive transition-colors uppercase tracking-wide text-sm font-medium ${scrolled ? 'text-charcoal' : 'text-white'}`}>
              Contact
            </a>
            {isAuthenticated ? (
              <Link to={isTrainer ? "/trainer" : "/dashboard"}>
                <Button className="btn-primary" data-testid="dashboard-btn">
                  {isTrainer ? "Trainer Dashboard" : "My Dashboard"}
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button className="btn-primary" data-testid="login-btn">
                  Client Login
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-charcoal"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-off-white/95 backdrop-blur-md py-6 px-4 rounded-b-2xl shadow-lg"
          >
            <div className="flex flex-col gap-4">
              <Link to="/programs" className="text-charcoal hover:text-olive py-2 uppercase tracking-wide text-sm font-medium">Programs</Link>
              <a href="#about" className="text-charcoal hover:text-olive py-2 uppercase tracking-wide text-sm font-medium">About</a>
              <a href="#trainer" className="text-charcoal hover:text-olive py-2 uppercase tracking-wide text-sm font-medium">Trainer</a>
              <Link to="/faq" className="text-charcoal hover:text-olive py-2 uppercase tracking-wide text-sm font-medium">FAQ</Link>
              <a href="#contact" className="text-charcoal hover:text-olive py-2 uppercase tracking-wide text-sm font-medium">Contact</a>
              {isAuthenticated ? (
                <Link to={isTrainer ? "/trainer" : "/dashboard"} className="btn-primary text-center">
                  {isTrainer ? "Trainer Dashboard" : "My Dashboard"}
                </Link>
              ) : (
                <Link to="/login" className="btn-primary text-center">
                  Client Login
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

// Hero Section with Next Intake Info
const HeroSection = ({ nextIntakes }) => {
  const foundationsIntake = nextIntakes?.find(i => i.program_name?.includes('Foundations'));
  const developmentIntake = nextIntakes?.find(i => i.program_name?.includes('Development'));
  
  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-end pb-12 md:pb-16">
      <div className="absolute inset-0 z-0">
        <img
          src={HERO_IMAGE}
          alt="Young puppy in training"
          className="w-full h-full object-cover object-[50%_28%]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-charcoal/30 to-charcoal/30" />
      </div>
      
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          {/* Main Content - Left Side */}
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="flex-1"
          >
            <motion.h1
              variants={fadeInUp}
              className="font-heading text-5xl md:text-7xl lg:text-8xl text-white leading-tight mb-3"
              style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.5)' }}
            >
              The Pack Standard
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-sand uppercase tracking-[0.3em] text-2xl md:text-4xl lg:text-5xl mb-5 font-medium">
              PUPPY ACADEMY
            </motion.p>
            <motion.p
              variants={fadeInUp}
              className="text-sand uppercase tracking-[0.2em] text-base md:text-lg mb-6 font-medium"
            >
              Socialisation · Manners · Obedience
            </motion.p>
            <motion.p
              variants={fadeInUp}
              className="text-white/80 text-lg md:text-xl leading-relaxed mb-6 max-w-xl"
            >
              <span className="block">Structured 4-week development programs</span>
              <span className="block">for your puppy aged 12-20 weeks.</span>
            </motion.p>
            
            {/* Key outcomes */}
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-x-6 gap-y-2 mb-6 text-white/90">
              <span className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-olive"></span>
                Calm behaviour
              </span>
              <span className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-olive"></span>
                Confident socialisation
              </span>
              <span className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-olive"></span>
                A foundation for life
              </span>
            </motion.div>
            
            {/* Location badge */}
            <motion.div variants={fadeInUp} className="flex items-center gap-2 mb-6 text-sand">
              <MapPin className="w-4 h-4" />
              <span className="uppercase tracking-widest text-sm">Sunshine Coast Hinterland</span>
            </motion.div>
            
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <Link to="/programs">
                <Button className="btn-primary text-lg" data-testid="hero-programs-btn">
                  View Programs
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/faq">
                <Button className="btn-outline border-white text-white hover:bg-white hover:text-charcoal text-lg" data-testid="hero-faq-btn">
                  Learn More
                </Button>
              </Link>
              <a 
                href="https://m.me/ThePackStandard" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="hero-messenger-btn"
              >
                <Button className="bg-olive hover:bg-olive-dark text-white text-lg">
                  <MessageCircle className="mr-2 w-5 h-5" />
                  Ask a Question
                </Button>
              </a>
            </motion.div>
          </motion.div>
          
          {/* Next Intake Badges - Right Side */}
          <div className="flex flex-col gap-4">
            {foundationsIntake && (
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-white/10 backdrop-blur-md rounded-xl p-6 md:min-w-[280px]"
              >
                <p className="text-white/80 text-sm mb-1">Next Intake</p>
                <p className="text-white font-heading text-xl mb-2">{foundationsIntake.program_name}</p>
                <p className="text-white/60 text-xs mb-2">12-16 weeks</p>
                <div className="space-y-1">
                  <p className="text-sand font-medium">{foundationsIntake.days_until_start} days away</p>
                  <p className="text-white/90 font-medium">
                    {foundationsIntake.is_fully_booked ? "Join the Waitlist!" : "Secure Your Spot Now!"}
                  </p>
                </div>
                <Link to="/programs" className="block mt-4">
                  <Button className={foundationsIntake.is_fully_booked ? "btn-outline border-white text-white hover:bg-white hover:text-charcoal w-full" : "btn-primary w-full"}>
                    {foundationsIntake.is_fully_booked ? "Join Waitlist" : "Enroll Now"}
                  </Button>
                </Link>
              </motion.div>
            )}
            
            {developmentIntake && (
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
                className="bg-white/10 backdrop-blur-md rounded-xl p-6 md:min-w-[280px]"
              >
                <p className="text-white/80 text-sm mb-1">Next Intake</p>
                <p className="text-white font-heading text-xl mb-2">{developmentIntake.program_name}</p>
                <p className="text-white/60 text-xs mb-2">16-20 weeks</p>
                <div className="space-y-1">
                  <p className="text-sand font-medium">{developmentIntake.days_until_start} days away</p>
                  <p className="text-white/90 font-medium">
                    {developmentIntake.is_fully_booked ? "Join the Waitlist!" : "Secure Your Spot Now!"}
                  </p>
                </div>
                <Link to="/programs" className="block mt-4">
                  <Button className={developmentIntake.is_fully_booked ? "btn-outline border-white text-white hover:bg-white hover:text-charcoal w-full" : "btn-primary w-full"}>
                    {developmentIntake.is_fully_booked ? "Join Waitlist" : "Enroll Now"}
                  </Button>
                </Link>
              </motion.div>
            )}
            
            {!foundationsIntake && !developmentIntake && nextIntakes?.[0] && (
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-white/10 backdrop-blur-md rounded-xl p-6 md:min-w-[280px]"
              >
                <p className="text-white/80 text-sm mb-1">Next Intake</p>
                <p className="text-white font-heading text-xl mb-2">{nextIntakes[0].program_name}</p>
                <div className="space-y-1">
                  <p className="text-sand font-medium">{nextIntakes[0].days_until_start} days away</p>
                  <p className="text-white/90 font-medium">Secure Your Spot Now!</p>
                </div>
                <Link to="/programs" className="block mt-4">
                  <Button className="btn-primary w-full">
                    Enroll Now
                  </Button>
                </Link>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Three Fundamental Elements Section
const FundamentalsSection = ({ fundamentals }) => {
  const fundamentalIcons = {
    socialisation: <Users className="w-12 h-12" strokeWidth={1.5} />,
    manners: <Heart className="w-12 h-12" strokeWidth={1.5} />,
    obedience: <Target className="w-12 h-12" strokeWidth={1.5} />
  };

  if (!fundamentals) return null;

  return (
    <section id="philosophy" data-testid="fundamentals-section" className="py-24 md:py-32 bg-sand text-charcoal">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-olive uppercase tracking-[0.2em] text-sm mb-4 font-medium">Our Approach</p>
          <h2 className="font-heading text-4xl md:text-5xl text-charcoal">
            The Three Fundamental
            <span className="block italic">Elements</span>
          </h2>
          <p className="text-stone text-lg mt-4 max-w-2xl mx-auto">
            Every aspect of our training is built around three fundamental elements that create a well-rounded, confident dog.
          </p>
        </motion.div>

        <div className="space-y-16">
          {Object.entries(fundamentals).map(([key, fundamental], index) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl p-8 md:p-12 border border-olive/10"
              data-testid={`fundamental-${key}`}
            >
              <div className="flex flex-col md:flex-row gap-8">
                {/* Icon and Title */}
                <div className="md:w-1/3">
                  <div className="text-olive mb-4">{fundamentalIcons[key]}</div>
                  <h3 className="font-heading text-3xl text-charcoal mb-3">{fundamental.title}</h3>
                  <p className="text-olive text-lg font-medium">{fundamental.tagline}</p>
                </div>
                
                {/* Content */}
                <div className="md:w-2/3">
                  <p className="text-charcoal/80 text-lg leading-relaxed mb-4">
                    {fundamental.description}
                  </p>
                  {fundamental.sub_description && (
                    <p className="text-stone mb-6 italic">
                      {fundamental.sub_description}
                    </p>
                  )}
                  
                  <p className="text-charcoal font-medium mb-4">{fundamental.skills_intro}</p>
                  <ul className="space-y-3 mb-6">
                    {fundamental.skills.map((skill, i) => (
                      <li key={`${fundamental.name}-skill-${i}`} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-olive mt-0.5 flex-shrink-0" />
                        <span className="text-charcoal/80">{skill}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <p className="text-stone italic border-l-2 border-olive pl-4">
                    {fundamental.goal}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Programs Overview Section
const ProgramsSection = ({ programs }) => {
  const programIcons = {
    puppy_foundations: <Heart className="w-8 h-8" strokeWidth={1.5} />,
    puppy_development: <GraduationCap className="w-8 h-8" strokeWidth={1.5} />
  };

  if (!programs) return null;

  return (
    <section id="programs" data-testid="programs-section" className="py-24 md:py-32 bg-off-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-olive uppercase tracking-[0.2em] text-sm mb-4 font-medium">Our Programs</p>
          <h2 className="font-heading text-4xl md:text-5xl text-charcoal">
            4-Week Development
            <span className="block italic">Programs</span>
          </h2>
          <p className="text-stone text-lg mt-4 max-w-2xl mx-auto">
            Each puppy attends one day per week for four weeks, learning alongside carefully matched companions.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8">
          {programs.map((program, index) => (
            <motion.div
              key={program.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="card-premium group"
              data-testid={`program-card-${program.id}`}
            >
              <div className="text-olive mb-4">{programIcons[program.id]}</div>
              <h3 className="font-heading text-2xl text-charcoal mb-2">{program.name}</h3>
              <p className="text-olive font-medium mb-3">{program.age_range}</p>
              <p className="text-stone leading-relaxed mb-6">{program.description}</p>
              <div className="flex flex-wrap gap-4 text-sm mb-6">
                <span className="bg-sand px-3 py-1 rounded-full text-charcoal flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {program.days.join(" or ")}
                </span>
                <span className="bg-sand px-3 py-1 rounded-full text-charcoal flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  Max {program.max_puppies}
                </span>
              </div>
              <div className="border-t border-olive/10 pt-4">
                <p className="text-sm text-stone mb-2">Focus Areas:</p>
                <div className="flex flex-wrap gap-2">
                  {program.focus_areas.slice(0, 4).map((area, i) => (
                    <span key={`${program.id}-focus-${i}`} className="text-xs bg-olive/10 text-olive px-2 py-1 rounded">
                      {area?.title || area}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <div className="inline-block bg-sand rounded-2xl p-8">
            <p className="font-heading text-4xl text-olive mb-2">$440</p>
            <p className="text-charcoal">per 4-week program</p>
            <p className="text-stone text-sm mt-2">($110 per session)</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Philosophy Section
const PhilosophySection = ({ philosophy }) => {
  if (!philosophy) return null;

  return (
    <section data-testid="philosophy-section" className="py-24 md:py-32 bg-sand">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-olive uppercase tracking-[0.2em] text-sm mb-4 font-medium">Our Philosophy</p>
            <h2 className="font-heading text-4xl md:text-5xl text-charcoal mb-8">
              Building Calm,
              <span className="block italic">Confident Dogs</span>
            </h2>
            
            <div className="space-y-6 mb-8">
              {philosophy.principles.map((principle, index) => (
                <div key={`principle-${principle.title}`} className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-olive/10 flex items-center justify-center shrink-0">
                    <Sparkles className="w-4 h-4 text-olive" />
                  </div>
                  <div>
                    <h4 className="font-medium text-charcoal">{principle.title}</h4>
                    <p className="text-stone text-sm mt-1">{principle.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              {philosophy.goals.map((goal, i) => (
                <span key={`goal-${i}-${goal.substring(0, 10)}`} className="bg-olive/10 text-olive px-4 py-2 rounded-full text-sm font-medium">
                  {goal}
                </span>
              ))}
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="relative"
          >
            <img
              src={PUPPY_TRAINING}
              alt="Puppy training session"
              className="w-full h-[500px] object-cover rounded-2xl"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      title: "Register Your Puppy",
      description: "Create an account and add your puppy's details including age, breed, and vaccination status."
    },
    {
      number: "02",
      title: "Choose Your Program",
      description: "Select the program that matches your puppy's age and pick an available intake."
    },
    {
      number: "03",
      title: "We Collect & Return",
      description: "We pick up your puppy in the morning and return them safely home in the afternoon."
    },
    {
      number: "04",
      title: "Track Progress",
      description: "Receive weekly report cards and homework assignments through your personal portal."
    }
  ];

  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="py-24 md:py-32 bg-off-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-olive uppercase tracking-[0.2em] text-sm mb-4 font-medium">The Process</p>
          <h2 className="font-heading text-4xl md:text-5xl text-charcoal">
            How It
            <span className="block italic">Works</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="text-center"
              data-testid={`step-${index}`}
            >
              <div className="font-heading text-6xl text-olive/20 mb-4">{step.number}</div>
              <h3 className="font-heading text-xl text-charcoal mb-3">{step.title}</h3>
              <p className="text-stone leading-relaxed">{step.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// What's Included Section
const IncludedSection = () => {
  const features = [
    { icon: <Truck className="w-6 h-6" strokeWidth={1.5} />, title: "Home Pickup & Drop-off", text: "Within our service area" },
    { icon: <Shield className="w-6 h-6" strokeWidth={1.5} />, title: "Individual Crate Transport", text: "Calm, safe arrivals" },
    { icon: <Users className="w-6 h-6" strokeWidth={1.5} />, title: "Small Group Sizes", text: "Max 4-6 per group" },
    { icon: <BookOpen className="w-6 h-6" strokeWidth={1.5} />, title: "Weekly Report Cards", text: "Detailed progress tracking" },
    { icon: <CheckCircle className="w-6 h-6" strokeWidth={1.5} />, title: "Homework Assignments", text: "Continue training at home" },
    { icon: <GraduationCap className="w-6 h-6" strokeWidth={1.5} />, title: "Graduation Certificate", text: "Shareable achievement" }
  ];

  return (
    <section data-testid="included-section" className="py-24 md:py-32 bg-sand">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="text-olive uppercase tracking-[0.2em] text-sm mb-4 font-medium">Everything Included</p>
          <h2 className="font-heading text-4xl md:text-5xl text-charcoal">
            A Complete
            <span className="block italic">Experience</span>
          </h2>
        </motion.div>
        
        <div className="grid md:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={`feature-${feature.title}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="bg-white/50 rounded-xl p-6 flex gap-4 items-start"
            >
              <div className="text-olive">{feature.icon}</div>
              <div>
                <h4 className="font-medium text-charcoal">{feature.title}</h4>
                <p className="text-stone text-sm">{feature.text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Health, Safety & Learning Environment Section
const EnvironmentSection = () => (
  <section data-testid="environment-section" className="py-24 md:py-32 bg-off-white">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      {/* Main Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-16"
      >
        <p className="text-olive uppercase tracking-[0.2em] text-sm mb-4 font-medium">Health & Safety</p>
        <h2 className="font-heading text-4xl md:text-5xl text-charcoal">
          Puppy Health, Safety &
          <span className="block italic">Learning Environment</span>
        </h2>
      </motion.div>

      {/* Introduction */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="max-w-4xl mx-auto mb-16"
      >
        <p className="text-xl text-charcoal font-medium text-center mb-8">
          Your puppy's health, safety and wellbeing are a top priority at The Pack Standard.
        </p>
        <div className="space-y-4 text-stone leading-relaxed">
          <p>
            Puppies attending day training spend time at our private Sunshine Coast Hinterland property in Montville, set on 2.5 acres of peaceful acreage. Rather than a busy commercial facility, puppies learn in a calm home environment where they can safely explore natural surroundings while receiving structured training and guidance.
          </p>
          <p>
            Experiencing grass, natural surfaces, new scents and gentle outdoor sounds helps puppies build confidence, curiosity and resilience during this important stage of development. The setting allows puppies to practise social skills and everyday behaviours in a relaxed, real-world environment.
          </p>
          <p>
            All activities are carefully supervised and structured to ensure a safe, positive and enriching experience for every puppy. This is a <strong className="text-charcoal">structured puppy education program</strong> rather than a traditional daycare environment, allowing each puppy to receive individual guidance, appropriate rest and meaningful learning throughout the day. The environment is carefully managed to provide safe exploration, structured learning and appropriate rest periods for young puppies.
          </p>
        </div>
      </motion.div>

      {/* Why Our Environment Benefits Puppies */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="bg-charcoal rounded-2xl p-8 md:p-12 text-white"
      >
        <div className="flex items-center gap-4 mb-8">
          <div className="w-14 h-14 rounded-full bg-olive/20 flex items-center justify-center">
            <Sparkles className="w-7 h-7 text-olive" strokeWidth={1.5} />
          </div>
          <h3 className="font-heading text-2xl md:text-3xl">Why Our Environment Benefits Puppies</h3>
        </div>
        
        <div className="space-y-4 text-white/80 leading-relaxed">
          <p>
            The early months of a puppy's life — particularly between <strong className="text-white">12 and 20 weeks of age</strong> — are a critical learning and socialisation period. Positive experiences during this time help shape how puppies respond to new environments, people and other dogs throughout their lives.
          </p>
          <p>
            A calm, natural setting allows puppies to safely experience new sights, sounds, textures and scents while being supported through structured training and gentle guidance. Time spent outdoors on grass and natural surfaces encourages exploration, curiosity and environmental confidence.
          </p>
          <p>
            Combined with small group sizes, careful supervision and purposeful training, this environment helps puppies learn appropriate social behaviour, develop emotional balance and build the confidence needed to navigate the world around them. The result is a puppy that is better prepared for family life, everyday environments and ongoing training as they grow.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-8 pt-8 border-t border-white/10">
          {[
            { icon: <MapPin className="w-6 h-6" />, title: "2.5 Acres", text: "Peaceful Hinterland property" },
            { icon: <Shield className="w-6 h-6" />, title: "Supervised", text: "Carefully structured activities" },
            { icon: <Heart className="w-6 h-6" />, title: "Calm Setting", text: "Home environment, not commercial" }
          ].map((item) => (
            <div key={`env-${item.title}`} className="flex items-start gap-3">
              <div className="text-olive mt-1">{item.icon}</div>
              <div>
                <h4 className="font-medium text-white">{item.title}</h4>
                <p className="text-white/60 text-sm">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);

// Hygiene Section - F10SC
const HygieneSection = () => (
  <section data-testid="hygiene-section" className="py-24 md:py-32 bg-sand">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid md:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-olive uppercase tracking-[0.2em] text-sm mb-4 font-medium">Hygiene Standards</p>
          <h2 className="font-heading text-4xl md:text-5xl text-charcoal mb-6">
            Veterinary-Grade
            <span className="block italic">Cleanliness</span>
          </h2>
          <div className="space-y-4 text-stone leading-relaxed">
            <p>
              Your puppy's health and wellbeing are a top priority at The Pack Standard. All training areas are carefully cleaned and disinfected after every session using <strong className="text-charcoal">F10SC Veterinary Disinfectant</strong>, a trusted veterinary-grade disinfectant widely used in animal care environments.
            </p>
            <p>
              F10SC is effective against a broad range of bacteria, viruses, fungi and spores, including <strong className="text-charcoal">Canine Parvovirus</strong>, which is particularly important when working with young puppies. This ensures a clean, hygienic and safe training environment for every puppy that attends.
            </p>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="bg-charcoal rounded-2xl p-8 text-white"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 rounded-full bg-olive/20 flex items-center justify-center">
              <Shield className="w-7 h-7 text-olive" strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="font-heading text-2xl">F10SC Veterinary Disinfectant</h3>
              <p className="text-white/60 text-sm">Veterinary-grade protection</p>
            </div>
          </div>
          <ul className="space-y-3">
            {[
              "Effective against Canine Parvovirus",
              "Kills bacteria, viruses, fungi & spores",
              "Used in veterinary hospitals worldwide",
              "Safe for use around animals",
              "Applied after every training session"
            ].map((item, i) => (
              <li key={`hygiene-${i}`} className="flex items-center gap-3 text-white/80">
                <CheckCircle className="w-5 h-5 text-olive flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </div>
  </section>
);

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "The transformation in our puppy's confidence after just 4 weeks was incredible. The structured approach really works.",
      author: "Sarah M.",
      puppy: "Luna, Golden Retriever"
    },
    {
      quote: "Having the pickup and drop-off service made it so easy. The weekly report cards helped us continue training at home.",
      author: "James T.",
      puppy: "Max, Border Collie"
    },
    {
      quote: "The focus on calm behaviour rather than just tricks was exactly what we needed. Our puppy is so well-mannered now.",
      author: "Michelle K.",
      puppy: "Bella, Labrador"
    }
  ];

  return (
    <section data-testid="testimonials-section" className="py-24 md:py-32 bg-off-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-olive uppercase tracking-[0.2em] text-sm mb-4 font-medium">Testimonials</p>
          <h2 className="font-heading text-4xl md:text-5xl text-charcoal">
            What Our
            <span className="block italic">Families Say</span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={`testimonial-${testimonial.author}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="card-premium"
              data-testid={`testimonial-${index}`}
            >
              <div className="flex gap-1 text-olive mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={`star-${testimonial.author}-${i}`} className="w-4 h-4 fill-olive" strokeWidth={1.5} />
                ))}
              </div>
              <p className="text-charcoal leading-relaxed mb-6 italic">"{testimonial.quote}"</p>
              <div className="border-t border-olive/20 pt-4">
                <p className="font-medium text-charcoal">{testimonial.author}</p>
                <p className="text-sm text-stone">{testimonial.puppy}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => (
  <section id="about" data-testid="about-section" className="py-24 md:py-32 bg-off-white">
    <div className="max-w-4xl mx-auto px-6 md:px-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-12"
      >
        <p className="text-olive uppercase tracking-[0.2em] text-sm mb-4 font-medium">About Us</p>
        <h2 className="font-heading text-4xl md:text-5xl text-charcoal">
          Our
          <span className="block italic">Mission</span>
        </h2>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="space-y-6 text-stone leading-relaxed"
      >
        <p>
          At The Pack Standard - Puppy Academy, our focus is simple: helping puppies grow into calm, confident and well-mannered family dogs.
        </p>
        <p>
          Through our Puppy Academy, we guide pups during the most important stage of their development, using a structured training approach built around Socialisation, Manners and Obedience.
        </p>
        <p>
          Our programs are designed to give puppies the skills they need to succeed in the real world. Rather than chaotic daycare environments, our Puppy Academy provides small group learning, structured social exposure and clear guidance, helping pups develop confidence while learning appropriate behaviour around people and other dogs.
        </p>
        <p>
          We believe that great dogs are created through consistency, calm leadership and clear communication. By focusing on engagement with people, emotional balance and everyday manners, we help set puppies up for a lifetime of success.
        </p>
        <p>
          The Pack Standard offers a boutique puppy training experience, with small group sizes and personalised attention for every puppy. Each program supports both the puppy and the owner, providing progress updates and guidance so learning continues at home.
        </p>
        <p className="text-charcoal font-medium">
          Our goal is to raise dogs that are not only obedient, but also balanced, socially confident and a joy to live with.
        </p>
      </motion.div>
    </div>
  </section>
);

// Meet the Trainer Section
const TrainerSection = () => (
  <section id="trainer" data-testid="trainer-section" className="py-24 md:py-32 bg-sand">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="order-2 md:order-1"
        >
          <p className="text-olive uppercase tracking-[0.2em] text-sm mb-4 font-medium">Meet the Trainer</p>
          <h2 className="font-heading text-4xl md:text-5xl text-charcoal mb-8">
            Emma
            <span className="block italic">Thompson</span>
          </h2>
          
          <div className="space-y-4 text-stone leading-relaxed">
            <p>
              Hi, My name is Emma Thompson and I am the trainer behind The Pack Standard - Puppy Academy.
            </p>
            <p>
              I created The Pack Standard because I believe puppies deserve more than just being looked after — they deserve the right foundation from the very beginning. Early training has such a powerful impact on the kind of dog a puppy grows into, and I'm passionate about helping owners raise dogs that are calm, confident and easy to live with.
            </p>
            <p>
              Our approach is centred around Socialisation, Manners and Obedience. I focus on structured learning, clear communication and calm guidance, helping puppies develop the skills they need to thrive both at home and out in the real world.
            </p>
            <p>
              Through our Puppy Academy, I work with puppies during the most important stages of their development, using small group programs designed to build confidence, encourage appropriate social behaviour and create strong engagement with people.
            </p>
            <p>
              I understand how overwhelming puppyhood can feel for owners, and my goal is to make that journey feel supported, structured and positive. I love helping puppies learn, but I also love helping owners feel more confident and connected with their dog.
            </p>
            <p className="text-charcoal font-medium">
              At The Pack Standard - Puppy Academy, my aim is simple: to help set puppies up for a lifetime of success.
            </p>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="order-1 md:order-2"
        >
          <img
            src={TRAINER_IMAGE}
            alt="Emma Thompson - Trainer"
            className="w-full h-[500px] md:h-[600px] object-cover rounded-2xl shadow-xl"
          />
        </motion.div>
      </div>
    </div>
  </section>
);

// CTA Section
const CTASection = ({ nextIntakes }) => {
  return (
    <section data-testid="cta-section" className="py-24 md:py-32 bg-charcoal text-white">
      <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="font-heading text-4xl md:text-5xl mb-6">
            Ready to Start Your
            <span className="block italic">Puppy's Journey?</span>
          </h2>
          <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">
            Spaces are limited to ensure quality attention for every puppy. 
            View our upcoming intakes and secure your spot today.
          </p>
          
          {nextIntakes && nextIntakes.length > 0 && (
            <div className="grid md:grid-cols-2 gap-6 mb-8 max-w-3xl mx-auto">
              {nextIntakes.slice(0, 2).map((intake, index) => (
                <motion.div
                  key={intake.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/10 backdrop-blur-md rounded-xl p-6"
                >
                  <p className="text-white/80 text-sm mb-2">Next Available Intake</p>
                  <p className="text-white font-heading text-2xl mb-2">{intake.program_name}</p>
                  <p className="text-sand text-sm mb-1">{intake.days_until_start} days away</p>
                  <p className="text-olive font-medium mb-4">Secure Your Spot Now!</p>
                  <Link to="/programs">
                    <Button className="btn-primary w-full">
                      Enroll Now
                    </Button>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
          
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/programs">
              <Button className="btn-terracotta text-lg" data-testid="cta-programs-btn">
                View All Programs
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => (
  <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-sand">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid md:grid-cols-2 gap-12">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-olive uppercase tracking-[0.2em] text-sm mb-4 font-medium">Get in Touch</p>
          <h2 className="font-heading text-4xl md:text-5xl text-charcoal mb-6">
            Questions?
          </h2>
          <p className="text-stone text-lg leading-relaxed mb-8">
            We'd love to hear from you. Reach out with any questions about our puppy programs.
          </p>
          
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-olive/10 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-olive" strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-medium text-charcoal">Location</p>
                <p className="text-stone">Western Avenue, Montville QLD 4560</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-olive/10 flex items-center justify-center">
                <Phone className="w-5 h-5 text-olive" strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-medium text-charcoal">Phone</p>
                <a href="tel:0451543647" className="text-charcoal/80 hover:text-olive transition-colors">
                  0451 543 647 <span className="text-charcoal/70">(0451 54 DOGS)</span>
                </a>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-olive/10 flex items-center justify-center">
                <Mail className="w-5 h-5 text-olive" strokeWidth={1.5} />
              </div>
              <div>
                <p className="font-medium text-charcoal">Email</p>
                <a href="mailto:thepackstandard@gmail.com" className="text-charcoal/80 hover:text-olive transition-colors">
                  thepackstandard@gmail.com
                </a>
              </div>
            </div>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          <img
            src={PUPPY_GROUP}
            alt="Puppy in training"
            className="w-full h-full min-h-[400px] object-cover rounded-2xl"
          />
        </motion.div>
      </div>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer data-testid="footer" className="py-12 bg-charcoal text-white">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="text-center md:text-left">
          <p className="font-heading text-2xl italic mb-1">The Pack Standard</p>
          <p className="text-olive text-sm tracking-wider">PUPPY ACADEMY</p>
        </div>
        <div className="flex gap-8 text-sm text-white/60">
          <Link to="/programs" className="hover:text-white transition-colors">Programs</Link>
          <Link to="/faq" className="hover:text-white transition-colors">FAQ</Link>
          <a href="#contact" className="hover:text-white transition-colors">Contact</a>
        </div>
        <p className="text-white/40 text-sm">
          © {new Date().getFullYear()} The Pack Standard. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

// Main Landing Page Component
const LandingPage = () => {
  const [programData, setProgramData] = useState(null);
  const [nextIntakes, setNextIntakes] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [programsRes, intakesRes] = await Promise.all([
          axios.get(`${API}/programs`),
          axios.get(`${API}/intakes/next`)
        ]);
        setProgramData(programsRes.data);
        setNextIntakes(intakesRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-off-white" data-testid="landing-page">
      <Navigation />
      <HeroSection nextIntakes={nextIntakes} />
      <FundamentalsSection fundamentals={programData?.fundamentals} />
      <ProgramsSection programs={programData?.programs} />
      <AboutSection />
      <TrainerSection />
      <PhilosophySection philosophy={programData?.philosophy} />
      <HowItWorksSection />
      <IncludedSection />
      <EnvironmentSection />
      <HygieneSection />
      <TestimonialsSection />
      <CTASection nextIntakes={nextIntakes} />
      <ContactSection />
      <Footer />
    </div>
  );
};

export default LandingPage;
